import { useMemo, useCallback, useState } from 'react';
import { useAuthContext } from './contexts.tsx';

interface UserInfo {
  name: string;
  email: string;
}

const USER_STORAGE_KEY = 'auth_user';

function loadStoredUser(): UserInfo | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

/**
 * Shell-facing auth hook.
 * Adapts useAuthContext into the shape the shell app expects.
 * Persists both token and user info to localStorage for session survival.
 */
export function useAuth() {
  const ctx = useAuthContext();
  const [user, setUser] = useState<UserInfo | null>(loadStoredUser);

  const login = useCallback(
    // Two call shapes: (token, userInfo) from dev login, or (email, password)
    // delegated to the context login.
    async (tokenOrEmail: string, userOrPassword?: UserInfo | string) => {
      // If called with a user object directly (from dev login), set it
      if (userOrPassword && typeof userOrPassword === 'object') {
        ctx.setToken(tokenOrEmail);
        const userInfo: UserInfo = {
          name: userOrPassword.name || 'User',
          email: userOrPassword.email || '',
        };
        setUser(userInfo);
        if (typeof window !== 'undefined') {
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userInfo));
        }
        return;
      }
      // Otherwise delegate to context login (email + password).
      return ctx.login(tokenOrEmail, userOrPassword ?? '');
    },
    [ctx]
  );

  const logout = useCallback(() => {
    ctx.logout();
    setUser(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(USER_STORAGE_KEY);
    }
  }, [ctx]);

  return useMemo(
    () => ({
      isAuthenticated: ctx.isAuthenticated,
      accessToken: ctx.token,
      user: ctx.isAuthenticated ? (user || { name: 'User', email: '' }) : null,
      login,
      logout,
    }),
    [ctx.isAuthenticated, ctx.token, user, login, logout]
  );
}
